import React from 'react';
import { Box, ButtonBase, Stack, Typography, alpha, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { WorkflowStepMeta } from '../../config/workflowUi';
import { borderRadius, layoutGap } from '../../../theme/theme';

/** Where a step stands relative to the run the user is actually in. */
export type WorkflowStepState = 'active' | 'complete' | 'reached' | 'locked';

export interface WorkflowWizardStepProps {
  step: WorkflowStepMeta;
  state: WorkflowStepState;
  /** A finished step drawn quietly once the workspace owns the screen. */
  muted: boolean;
  compact: boolean;
  clickable: boolean;
  /** Navigation is held because the current step is still working. */
  blocked: boolean;
  onSelect: () => void;
}

/**
 * The name a step that cannot be entered answers to. The reason is carried in
 * words, so neither the dimmed colour nor the cursor has to say it alone.
 */
export const describeUnavailableStep = (label: string, blocked: boolean): string =>
  blocked ? `${label} step unavailable while the current operation finishes` : `${label} step not available yet`;

const resolveDotColor = (theme: Theme, state: WorkflowStepState, muted: boolean): string => {
  if (state === 'active') {
    return theme.palette.primary.main;
  }
  if (state === 'complete') {
    return muted
      ? alpha(theme.palette.text.secondary, 0.45)
      : theme.palette.success.main;
  }
  return alpha(theme.palette.text.secondary, 0.35);
};

const resolveLabelColor = (theme: Theme, state: WorkflowStepState, muted: boolean): string => {
  if (state === 'active') {
    return theme.palette.text.primary;
  }
  if (state === 'complete' && !muted) {
    return theme.palette.text.secondary;
  }
  return alpha(theme.palette.text.secondary, 0.55);
};

const WorkflowWizardStep: React.FC<WorkflowWizardStepProps> = ({
  step,
  state,
  muted,
  compact,
  clickable,
  blocked,
  onSelect,
}) => {
  const theme = useTheme();
  const isActive = state === 'active';
  const isLocked = state === 'locked';
  const dotColor = resolveDotColor(theme, state, muted);

  const dot = (
    <Box
      data-testid={`workflow-step-dot-${step.screen}`}
      data-step-state={state}
      sx={{
        width: compact ? { xs: 6, sm: 7, md: 8 } : 12,
        height: compact ? { xs: 6, sm: 7, md: 8 } : 12,
        borderRadius: '50%',
        boxSizing: 'border-box',
        // A step never reached is hollow: the shape says "locked", not the hue.
        backgroundColor: isLocked ? 'transparent' : dotColor,
        border: isLocked ? `1.5px solid ${dotColor}` : 'none',
        flexShrink: 0,
        boxShadow: isActive ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.22)}` : 'none',
      }}
    />
  );

  const content = compact ? (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: { xs: 10, sm: 14, md: 16, lg: 20 },
        minHeight: { xs: 18, sm: 20, md: 22 },
        borderRadius: '50%',
      }}
    >
      {dot}
    </Box>
  ) : (
    <Stack direction="row" spacing={layoutGap.dense} alignItems="center" sx={{ flexShrink: 0 }}>
      {dot}
      <Typography
        variant="caption"
        sx={{
          fontWeight: isActive ? 700 : 500,
          color: resolveLabelColor(theme, state, muted),
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
        }}
      >
        {step.label}
      </Typography>
    </Stack>
  );

  const shapeSx = compact
    ? { display: 'inline-flex', borderRadius: `${borderRadius.full}px`, p: 0 }
    : {
        display: 'inline-flex',
        borderRadius: `${borderRadius.sm}px`,
        px: layoutGap.micro,
        py: 0.125,
        textAlign: 'inherit' as const,
      };

  if (isActive) {
    return (
      <Box aria-current="step" sx={{ ...shapeSx, cursor: 'default' }}>
        {content}
      </Box>
    );
  }

  if (clickable) {
    return (
      <ButtonBase focusRipple onClick={onSelect} aria-label={`Open ${step.label} step`} sx={shapeSx}>
        {content}
      </ButtonBase>
    );
  }

  return (
    <ButtonBase
      disabled
      aria-label={describeUnavailableStep(step.label, blocked)}
      sx={{
        ...shapeSx,
        opacity: 0.75,
        cursor: 'not-allowed',
        // Keep the cursor and the step help reachable on a control that cannot act.
        '&.Mui-disabled': { pointerEvents: 'auto' },
      }}
    >
      {content}
    </ButtonBase>
  );
};

export default WorkflowWizardStep;
